import { useState, type FormEvent } from "react";
import { Button, Card, IconButton, Modal, ModalFooter, Row, SelectField, TextField, useToast } from "@plataforma/design-system";
import type { AvailabilitySlot, Location } from "@plataforma/shared";
import { errorMessage } from "../../lib/api";
import { weekdayLabels } from "../../lib/labels";
import { useCreateLocation, useLocations, useRemoveLocation, useSetAvailability } from "../agenda/api";

export function LocationsSection() {
  const locations = useLocations();
  const [adding, setAdding] = useState(false);
  return (
    <Card padded title="Locais e disponibilidade" actions={<Button size="small" onClick={() => setAdding(true)}>＋ Adicionar local</Button>}>
      <p className="ds-muted ds-small" style={{ marginTop: 12 }}>Adicione só os dias de atendimento de cada local. Horário fora da disponibilidade gera aviso ao marcar, sem impedir.</p>
      {(locations.data ?? []).map((location) => <LocationEditor key={`${location.id}-${JSON.stringify(location.availability)}`} location={location} />)}
      {adding ? <LocationForm onClose={() => setAdding(false)} /> : null}
    </Card>
  );
}

function LocationEditor({ location }: { location: Location }) {
  const toast = useToast();
  const save = useSetAvailability();
  const remove = useRemoveLocation();
  const [slots, setSlots] = useState<AvailabilitySlot[]>(location.availability);
  const [error, setError] = useState<string | null>(null);
  const free = weekdayLabels.map((_, weekday) => weekday).filter((weekday) => !slots.some((slot) => slot.weekday === weekday));
  const update = (index: number, change: Partial<AvailabilitySlot>) => setSlots(slots.map((slot, position) => (position === index ? { ...slot, ...change } : slot)));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const invalid = slots.find((slot) => !slot.start || !slot.end || slot.start >= slot.end);
    if (invalid) return setError(`Em ${weekdayLabels[invalid.weekday]}, o encerramento deve ser posterior à abertura.`);
    try {
      await save.mutateAsync({ id: location.id, slots });
      toast(`Disponibilidade de ${location.name} salva.`);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <form className="settings-location" onSubmit={submit}>
      <Row align="between">
        <strong>{location.name} <span className="ds-muted ds-small">· {location.kind === "remote" ? "remoto" : "presencial"}</span></strong>
        {!location.inUse ? <Button variant="quiet" tone="danger" onClick={() => remove.mutate(location.id, { onError: (failure) => toast(errorMessage(failure)) })}>Remover local</Button> : null}
      </Row>
      {slots.length === 0 ? <p className="ds-muted ds-small" style={{ margin: "10px 0" }}>Nenhum dia adicionado.</p> : null}
      {slots.map((slot, index) => (
        <div key={index} className="settings-slot">
          <select className="ds-input" aria-label="Dia de atendimento" value={slot.weekday} onChange={(event) => update(index, { weekday: Number(event.target.value) })}>
            {weekdayLabels.map((label, weekday) => <option key={label} value={weekday} disabled={weekday !== slot.weekday && !free.includes(weekday)}>{label}</option>)}
          </select>
          <input className="ds-input" type="time" aria-label={`Abertura de ${weekdayLabels[slot.weekday]}`} value={slot.start} onChange={(event) => update(index, { start: event.target.value })} />
          <input className="ds-input" type="time" aria-label={`Encerramento de ${weekdayLabels[slot.weekday]}`} value={slot.end} onChange={(event) => update(index, { end: event.target.value })} />
          <IconButton label={`Remover ${weekdayLabels[slot.weekday]}`} onClick={() => setSlots(slots.filter((_, position) => position !== index))}>−</IconButton>
        </div>
      ))}
      {error ? <p className="ds-field__error" role="alert">{error}</p> : null}
      <Row align="end" style={{ marginTop: 10 }}>
        <Button size="small" disabled={free.length === 0} onClick={() => setSlots([...slots, { weekday: free[0] ?? 0, start: "", end: "" }])}>＋ Adicionar dia</Button>
        <Button size="small" type="submit" variant="primary" disabled={save.isPending}>Salvar horários</Button>
      </Row>
    </form>
  );
}

function LocationForm({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const create = useCreateLocation();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"in_person" | "remote">("in_person");
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({ name, kind });
      toast("Local adicionado. Configure os dias e horários.");
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }
  return (
    <Modal title="Adicionar local de atendimento" onClose={onClose} dirty={name.length > 0}>
      <form onSubmit={submit}>
        <TextField label="Nome do local" required maxLength={60} value={name} onChange={(event) => setName(event.target.value)} error={error} />
        <SelectField label="Tipo" value={kind} onChange={(event) => setKind(event.target.value as "in_person" | "remote")}>
          <option value="in_person">Presencial</option>
          <option value="remote">Remoto</option>
        </SelectField>
        <ModalFooter>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>Adicionar local</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
